import React, { useContext, useState } from 'react';
import { useNavigate } from 'react-router';
import { styled } from 'styled-components';
import { useTranslation } from 'react-i18next';
import Text from '@ingka/text';
import Button from '@ingka/button';
import { mediaQueries } from '../styles/mediaQueries';
import AppFooter from '../components/AppFooter';
import PageContent from '../components/PageContent';
import StorePicker from '../components/StorePicker';
import PageTitle from '../components/PageTitle';
import Loading from '../components/Loading';
import useGetAvailableTimeslots from '../hooks/useGetAvailableTimeslots';
import RetryMessage from '../components/RetryMessage';
import apiServices from '../services';
import { showError } from '../reducers/error/errorReducer';
import { getLoginLink } from '../helpers/loginLinkHelper';
import {
  addPersistingQueryParamsToPath,
  getPath,
  getPrevPath,
} from '../helpers/urlHelper';
import { useStateDispatch, useStateSelector } from '../store/';
import { StyledWrapper } from '../components/PageLayout';
import StickyFooter from '../components/StickyFooter';
import SelectReplacementServiceConfirmation from '../components/SelectReplacementServiceConfirmation';
import {
  selectReplacementServiceSlots,
  selectSelectedTimeSlot,
  selectOriginalServiceSlots,
  selectSelectedOriginalService,
  selectServiceForTheSelectedTimeSlot,
  selectAppOperation,
} from '../reducers/selectors';
import { translationExists } from '../helpers/translationsHelper';
import { clearSelectedTimeSlot } from '../reducers/app/appReducer';
import TimeSlotPicker from '../components/TimeSlotPicker/TimeSlotPicker';
import LanguagePicker from '../components/LanguagePicker';
import { useStorePicker } from '../hooks/useStorePicker';
import { isLanguagePickerEnabled } from '../helpers/appHelper';
import ServiceHelpText from '../components/ServiceHelpText';
import { ViewContext } from '../context';

const { loginRequired } = CSS_CONFIG.services;

const SelectionContainer = styled.div<{ $isAltView: boolean }>`
  margin-top: 1.5rem;
  ${mediaQueries.portraitTabletUp} {
    margin: ${(props) => (props.$isAltView ? '2.5rem 4rem 0' : '2.5rem 0 0')};
  }
`;

const PickerContainer = styled.div<{
  $isAltView: boolean;
  $column?: boolean;
}>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 1rem;

  ${(props) =>
    !props.$isAltView &&
    !props.$column &&
    `
    ${mediaQueries.portraitTabletUp} {
      flex-direction: row;
      align-items: flex-end;
      gap: 3rem;
    }
  `}
`;

const StyledParagraph = styled(Text)`
  &&& {
    margin-top: 0px;
    white-space: pre-line;
  }
`;

const TimeSlotsPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const appOperation = useStateSelector(selectAppOperation);
  const view = useContext(ViewContext);
  const dispatch = useStateDispatch();
  const [reserveLoading, setReserveLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const isUpdatingPendingSlots = useStateSelector(
    (state) => state.timeSlots.pendingSlots.isUpdating,
  );
  const serviceId = useStateSelector((state) => state.app.selected.serviceId);
  const disabledPrevButton = useStateSelector(
    (state) => state.app.disabledPrevButton,
  );
  const isLoggedIn = useStateSelector((state) => state.user.isLoggedIn);
  const selectedOriginalTimeSlot = useStateSelector(
    (state) => state.app.selected.timeSlot,
  );
  const selectedTimeSlot = useStateSelector(selectSelectedTimeSlot);
  const isLoadingTimeSlots = useStateSelector(
    (state) => state.timeSlots.isLoading,
  );
  const slots = useStateSelector(selectOriginalServiceSlots);
  const replacementServiceSlots = useStateSelector(
    selectReplacementServiceSlots,
  );
  const from = useStateSelector((state) => state.timeSlots.from);
  const prevEnabled = useStateSelector((state) => state.timeSlots.prevEnabled);
  const selectedOriginalService = useStateSelector(
    selectSelectedOriginalService,
  );
  const languageTranslations = useStateSelector(
    (state) => state.app.languages.translations,
  );
  const showRetry = useStateSelector((state) => state.timeSlots.showRetry);

  const selectedService = useStateSelector(selectServiceForTheSelectedTimeSlot);
  const isViewAlt = view === 'alt';
  const isViewApp = view === 'app';

  const { shouldShowStorePicker, storeList } = useStorePicker(serviceId);

  const languagePickerIsEnabled =
    languageTranslations &&
    languageTranslations.length > 1 &&
    isLanguagePickerEnabled(serviceId, CSS_CONFIG);

  const { onRetry, handlePendingSlotSelected } = useGetAvailableTimeslots();

  const isLoginRequired = loginRequired.some(
    (service) => !isLoggedIn && service === selectedService?.serviceId,
  );

  const handleLogin = () => {
    const currentLocation = window.location.href.replace(
      '/timeslots',
      '/contact',
    );
    window.location.href = getLoginLink(currentLocation, CSS_CONFIG);
  };

  const handleNextStep = () => {
    const path = getPath(appOperation, 'contact', view);
    return isLoginRequired
      ? handleLogin()
      : navigate(addPersistingQueryParamsToPath(path));
  };

  const reserveTimeSlot = async (): Promise<void> => {
    setReserveLoading(true);
    const { id = '' } = selectedTimeSlot || {};
    const { error } = await apiServices.timeSlot.reserveTimeSlot(id);

    if (error) {
      setReserveLoading(false);
      dispatch(
        showError({
          title: t('pages.timeslots.reserveError.title'),
          description: t('pages.timeslots.reserveError.description'),
          button: t('pages.timeslots.reserveError.close'),
        }),
      );
      dispatch(clearSelectedTimeSlot());
      onRetry();
    } else {
      handleNextStep();
    }
  };

  const handleSubmit = async (
    e: React.SyntheticEvent<HTMLFormElement>,
  ): Promise<void> => {
    e.preventDefault();
    if (selectedOriginalTimeSlot) {
      await reserveTimeSlot();
      return;
    }
    setShowModal(true);
  };

  const handleContinueButton = async () => {
    await reserveTimeSlot();
  };

  const handleTimeSpanChange = (date: string): void => {
    dispatch(clearSelectedTimeSlot());
    onRetry(date);
  };

  const previousButtonIsVisible = disabledPrevButton !== 'timeslots';
  return (
    <>
      <PageTitle title={t('pages.timeslots.pageTitle')} />
      <form onSubmit={handleSubmit}>
        <StyledWrapper $withStickyFooter={isViewAlt || isViewApp}>
          <PageContent>
            <Text tagName="h2" textStyle="Heading.L">
              {isViewAlt
                ? t('pages.timeslots.plainViewTitle')
                : t(`services.name.${serviceId}`)}
            </Text>
            {!isViewAlt &&
              translationExists(`services.description.${serviceId}`, t) && (
                <StyledParagraph tagName="p" textStyle="Body.L">
                  {t(`services.description.${serviceId}`)}
                </StyledParagraph>
              )}
            <ServiceHelpText />
            <SelectionContainer $isAltView={isViewAlt}>
              <PickerContainer
                $isAltView={isViewAlt}
                $column={shouldShowStorePicker && storeList.length === 1}
              >
                {shouldShowStorePicker &&
                  (storeList.length === 1 ? (
                    <Text tagName="p" textStyle="Heading.M">
                      {storeList[0].text}
                    </Text>
                  ) : (
                    <StorePicker
                      serviceId={serviceId}
                      stores={storeList}
                      operation={appOperation}
                      locationType={selectedOriginalService?.location}
                      isDisabled={isLoadingTimeSlots || reserveLoading}
                    />
                  ))}
                {languagePickerIsEnabled && (
                  <LanguagePicker
                    languages={languageTranslations}
                    isDisabled={isLoadingTimeSlots || reserveLoading}
                    serviceId={serviceId}
                    operation={appOperation}
                  />
                )}
              </PickerContainer>
              {isLoadingTimeSlots || !selectedOriginalService ? (
                <Loading title={t('components.loading.title.timeslots')} />
              ) : (
                <>
                  {showRetry && (
                    <RetryMessage
                      message={t('pages.timeslots.errorMessage')}
                      onRetry={onRetry}
                    />
                  )}
                  {!showRetry && (
                    <TimeSlotPicker
                      slots={slots}
                      replacementServiceSlots={replacementServiceSlots}
                      shouldStartOn={from}
                      onPendingSlotSelected={handlePendingSlotSelected}
                      onNextClick={handleTimeSpanChange}
                      onPrevClick={handleTimeSpanChange}
                      prevEnabled={prevEnabled}
                      disabled={reserveLoading}
                    />
                  )}
                  {translationExists(
                    `pages.timeslots.subDescriptionFallback.${serviceId}`,
                    t,
                  ) && (
                    <StyledParagraph tagName="p" textStyle="Body.L">
                      {t(`pages.timeslots.subDescriptionFallback.${serviceId}`)}
                    </StyledParagraph>
                  )}
                </>
              )}
            </SelectionContainer>
            {isViewAlt || isViewApp ? (
              <StickyFooter>
                {isViewApp && previousButtonIsVisible && (
                  <Button
                    disabled={reserveLoading}
                    onClick={() =>
                      navigate(
                        addPersistingQueryParamsToPath(
                          getPrevPath(appOperation, view),
                        ),
                      )
                    }
                    text={t('components.stickyFooter.previousButton')}
                    type="secondary"
                    data-cy="previous"
                    aria-label={t(
                      'components.stickyFooter.ariaLabelBackToServices',
                    )}
                  />
                )}
                <Button
                  htmlType="submit"
                  id="nextButton"
                  disabled={
                    !selectedTimeSlot ||
                    isLoadingTimeSlots ||
                    isUpdatingPendingSlots
                  }
                  loading={reserveLoading}
                  text={t('components.stickyFooter.nextButton')}
                  type="emphasised"
                  aria-label={t(
                    'components.stickyFooter.ariaLabelToContactForm',
                  )}
                  data-cy="next-button"
                />
              </StickyFooter>
            ) : (
              <AppFooter
                prevVisible={previousButtonIsVisible}
                nextVisible
                isLoading={reserveLoading}
                nextDisabled={
                  !selectedTimeSlot ||
                  isLoadingTimeSlots ||
                  isUpdatingPendingSlots
                }
                nextText={
                  isLoginRequired ? t('components.appFooter.login') : ''
                }
                nextAriaLabel={
                  isLoginRequired
                    ? undefined
                    : t('components.stickyFooter.ariaLabelToContactForm')
                }
                prevAriaLabel={t(
                  'components.stickyFooter.ariaLabelBackToServices',
                )}
              />
            )}
            {selectedOriginalService &&
              selectedOriginalService.replacementService && (
                <SelectReplacementServiceConfirmation
                  onContinueButtonClick={handleContinueButton}
                  onCloseButtonClick={() => setShowModal(false)}
                  showModal={showModal}
                  selectedOriginalServiceInclSavingsPrice={
                    selectedOriginalService?.displayPrice?.inclSavings ?? 0
                  }
                  selectedOriginalServiceExclSavingsPrice={
                    selectedOriginalService?.displayPrice?.exclSavings ?? 0
                  }
                  replacementService={
                    selectedOriginalService.replacementService
                  }
                />
              )}
          </PageContent>
        </StyledWrapper>
      </form>
    </>
  );
};

export default TimeSlotsPage;
